import React, { useState, useEffect, useRef, useCallback } from 'react';
import styles from './LocationSummary.module.scss';

import {
  NoDataTableRow,
  Pagination,
  SearchField,
  SummaryHeaderWrapper,
  SummaryPageHeader,
  SummaryTableControls,
  SummaryWrapper,
  TableWrapper,
  TableHeadComponent
} from 'components/SummaryComponents';
import { StyledTableCell, StyledTableRow } from 'components/StyledComponents';
import { StyledButton, DownloadCSVButton } from 'components/Buttons';
import { FilterButton, FilterChips, FilterWrapper } from 'components/FilterComponentsV2';
import StyledLink from 'components/StyledLink';

import { Table, TableBody } from '@material-ui/core';

import { useHistory } from 'react-router-dom';
import { useMobileViewport } from 'hooks/useMobileViewport';
import { useUserConfig } from 'hooks/useUserConfig';
import { useLocationActions, useLocationSelector } from 'hooks/Location';
import { useCommonActions, useCommonSelector } from 'hooks/Common';
import useScrollManager from 'hooks/useScrollManager';

import {
  getChangedByApplyFilter,
  getChangedByLimitFilter,
  getChangedByPageFilter,
  getChangedBySearchFilter,
  getChangedBySortFilter
} from 'helpers/SummaryPagesHelpers';
import { fileDownload } from 'helpers/AppHelpers';

import {
  LOCATION_DETAILED_PATH,
  LOCATION_EDIT_PATH,
  BATCH_ASSET_UPDATES_PATH
} from 'constants/routeConstants';
import { LOCATION_LIST_TABLE_PARAMETER } from 'constants/configTableConstants';

import {
  generateChips,
  generateInitialQuery,
  useSummaryParams,
  parseQuery,
  tableConfig,
  removeChip
} from './helpers';
import FilterModal from './Filter';

export function LocationSummary() {
  const isMobile = useMobileViewport();
  const history = useHistory();
  const tableRef = useRef(null);

  const urlParams = useSummaryParams();

  const [tableState, setTableState] = useState([]);
  const [openFilter, setOpenFilter] = useState(false);
  const [chips, setChips] = useState([]);

  const { isConfigReceived, getTableLimit, isAllLimitEnabled } = useUserConfig();
  const { setLoadMoreFlagAction } = useCommonActions();
  const { loadMoreFlag } = useCommonSelector();

  const { filter, filterCriteria } = useLocationSelector();
  const {
    getLocationsAction,
    getFilterCriteriaAction,
    setFilterAction,
    getSummaryCsvResourceAction,
    clearStateAction
  } = useLocationActions();

  const { filters, pagination, sortRules, responseReceived } = filter;

  const { saveScrollPosition, removeScrollPosition } = useScrollManager({
    tableKey: LOCATION_LIST_TABLE_PARAMETER,
    isDataLoaded: responseReceived
  });

  const getLocations = useCallback(
    (query, isAutoload) => {
      setFilterAction(query);

      getLocationsAction(parseQuery(query)).then((tableData) => {
        setLocations(tableData, query, isAutoload);
        !loadMoreFlag && setLoadMoreFlagAction(false);
      });
    },
    [setFilterAction, getLocationsAction]
  );

  const setLocations = useCallback(
    (data, query, isAutoload) => {
      setTableState((prevState) =>
        isAutoload ? prevState.concat(data?.items || []) : data?.items || []
      );

      const updatedFilter = {
        ...query,
        pagination: { limit: data.limit, page: data.pageNumber, totalPages: data.totalPages },
        responseReceived: true
      };
      setFilterAction(updatedFilter);
    },
    [setFilterAction]
  );

  useEffect(() => {
    if (!isConfigReceived) return;

    getFilterCriteriaAction().then((criteria) => {
      const q = {
        getTableLimit,
        isAllLimitEnabled,
        filter,
        ...urlParams
      };
      const resultFilter = generateInitialQuery(q);
      getLocations(resultFilter);
      setChips(generateChips(criteria, resultFilter.filters));
    });
  }, [isConfigReceived]);

  useEffect(() => {
    return () => {
      clearStateAction();
    };
  }, []);

  const findMatches = useCallback(
    (value) => getLocations(getChangedBySearchFilter(filter, value)),
    [filter, getLocations]
  );
  const sortTable = useCallback(
    (rowName) => getLocations(getChangedBySortFilter(filter, rowName)),
    [filter, getLocations]
  );
  const applyFilter = useCallback(
    (values) => {
      getLocations(getChangedByApplyFilter(filter, values));
      setChips(generateChips(filterCriteria, values));
    },
    [filter, filterCriteria, getLocations]
  );
  const changeLimit = useCallback(
    () =>
      getLocations(getChangedByLimitFilter(filter, getTableLimit(LOCATION_LIST_TABLE_PARAMETER))),
    [filter, getTableLimit, getLocations]
  );
  const changePage = useCallback(
    (page, param) => getLocations(getChangedByPageFilter(filter, page), param === 'AUTOLOAD'),
    [filter, getLocations]
  );
  const deleteChip = useCallback(
    (chip) => applyFilter(removeChip(filters, chip)),
    [applyFilter, filters]
  );

  const openFilterModal = useCallback(() => setOpenFilter(true), [setOpenFilter]);

  const createLocation = useCallback(() => {
    removeScrollPosition();
    history.push(LOCATION_EDIT_PATH);
  }, [history]);

  const updateBatchLocation = useCallback(() => {
    removeScrollPosition();
    history.push(BATCH_ASSET_UPDATES_PATH);
  }, [history]);

  const downloadCsv = () =>
    getSummaryCsvResourceAction(filter).then((file) => file?.link && fileDownload(file));

  const HeaderControls = () => (
    <div className={styles.controls}>
      {!isMobile && <DownloadCSVButton onClick={downloadCsv} />}
      <StyledButton
        label="Batch location update"
        onClick={updateBatchLocation}
        classes={styles.main_button}
      />
      <StyledButton
        label="Add new location"
        onClick={createLocation}
        classes={styles.main_button}
      />
    </div>
  );

  return (
    <SummaryWrapper>
      <SummaryHeaderWrapper>
        <SummaryPageHeader title="Location Summary">
          {!isMobile && <HeaderControls />}
        </SummaryPageHeader>
        <SummaryTableControls>
          <FilterWrapper>
            <FilterButton onClick={openFilterModal} isActive={openFilter || !!chips?.length} />
            {!isMobile && <FilterChips chips={chips} onDelete={deleteChip} />}
          </FilterWrapper>
          <SearchField filterValue={filters?.searchQuery} onSearch={findMatches} />
        </SummaryTableControls>
      </SummaryHeaderWrapper>

      {isMobile && <HeaderControls />}

      <TableWrapper ref={tableRef}>
        <Table stickyHeader={!isMobile} className={styles.table}>
          <TableHeadComponent config={tableConfig} sortRules={sortRules} onSortApply={sortTable} />
          <TableBody>
            {tableState?.length ? (
              tableState.map((row) => (
                <StyledTableRow key={row.id} className={styles.location_table_row}>
                  <StyledTableCell className={styles.codeCell}>
                    <StyledLink
                      bold
                      to={`${LOCATION_DETAILED_PATH}/${row.id}`}
                      onClick={saveScrollPosition}>
                      {row?.siteCode || ''}
                    </StyledLink>
                  </StyledTableCell>
                  <StyledTableCell className={styles.addressCell}>
                    {row.city || ''}
                    {row.city && row.state && `, `}
                    {row.state || ''}
                  </StyledTableCell>
                  <StyledTableCell className={styles.numberCell}>
                    {row.locationJobNumber || ''}
                  </StyledTableCell>
                  <StyledTableCell className={styles.descriptionCell}>
                    {row.description}
                  </StyledTableCell>
                  <StyledTableCell className={styles.typeCell}>{row.siteType.name}</StyledTableCell>
                </StyledTableRow>
              ))
            ) : responseReceived ? (
              <NoDataTableRow isTableFiltered={!!chips?.length || !!filters?.searchQuery} />
            ) : null}
          </TableBody>
        </Table>
      </TableWrapper>

      <Pagination
        withOptionAll
        rowsNumber={tableState?.length}
        currentPage={pagination?.page}
        totalPages={pagination?.totalPages}
        onChangePage={changePage}
        onChangeLimit={changeLimit}
        selectedLimit={pagination?.limit}
        tableName={LOCATION_LIST_TABLE_PARAMETER}
        tableRef={tableRef}
      />

      {openFilter && (
        <FilterModal open={openFilter} setOpen={setOpenFilter} onApply={applyFilter} />
      )}
    </SummaryWrapper>
  );
}
